/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/checkin_alert.svg";
import __asset_1 from "@res/icons/checkin_complete.svg";
import {DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {formatStatus} from "./util";
import {AlertStatus, DB_Alert} from "@app/app-shared/alert";
import {padding_rl_med} from "@styles/styles";
import {COLORS} from "@res/colors";
import {HGap} from "../../widget/HGap";
import {FONTS} from "@res/fonts";
import * as React from "react";
import {ReactNode} from "react";
import {AlertsTableRow, tableCardStyle} from "../../home/AlertsTab";
import {TS_Table} from "../../../_TS_TABLE";
import * as emotion from "emotion";
import {TableHeader} from "../../../utils/TableHeader";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {DateTimesUtil} from "../patients/DateTimesUtil";
import {TableContent} from "../../../utils/TableRow";
import {CommentsModule} from "@modules/CommentsModule";
import {action, comment, complete, open} from "./AlertRow";

const checkin_alert_icon = __asset_0;
const checkin_complete_icon = __asset_1;
import {calculatePagination, PaginationComponent} from "../../widget/PaginationComponent";
import {PatientsContext, PatientsContextType, PatientsMap} from "../patients/PatientsContext";
import {DB_Patient} from "@app/app-shared/patient";
import {navigateToPatientPage} from "@modules/PatientDataModule";
import {Route_Alerts, Route_CheckIn} from "../../../Routes";
import {CheckinAlertType_AllDone, CheckinAlertType_Missed} from "@app/app-shared/check-in";
import {RoutingModule} from "@intuitionrobotics/thunderstorm/frontend";
import {headHeight, rowHeight} from "../patients/PatientsTable";

type Props = {
    content: AlertsTableRow[],
    patientsContext: PatientsContextType
}

type State = {
    page: number,
    sort: { filterColumn: keyof AlertsTableRow, invert: boolean }
}

export class AlertTable
    extends React.Component<Props, State> {

    private tableRef = React.createRef<HTMLDivElement>();

    private header: (keyof AlertsTableRow)[] = [
        "alert",
        "created",
        "lastUpdated",
        "careManager",
        "status"
    ];

    private sortObj = (name: "lastUpdated" | "created" | "alert" | "careManager" | "status") => ({
        func: () => this.setState(s => ({sort: {filterColumn: name, invert: !s.sort.invert}})),
        up: this.state.sort.filterColumn !== name ? undefined : this.state.sort.invert
    });


    constructor(props: Props) {
        super(props);

        this.state = {
            page: 1,
            sort: {filterColumn: "created", invert: false}
        }
    }

    resolveIcon = (alert: DB_Alert<any>): ReactNode => {
        // Check-in alerts get a dedicated icon regardless of status — same SVGs
        // used in the Check-in dashboard so the two views read as one feature.
        // Notification status is still shown in its own column, so no info loss.
        if (alert.alertType === CheckinAlertType_Missed)
            return <img alt={"check-in missed"} src={checkin_alert_icon} height={24}/>;
        if (alert.alertType === CheckinAlertType_AllDone)
            return <img alt={"check-in all done"} src={checkin_complete_icon} height={24}/>;

        switch (alert.status) {
            case AlertStatus.OPEN:
            case AlertStatus.NEW:
                return <img alt={"open"} src={open} height={24}/>;
            case AlertStatus.COMPLETE:
                return <img alt={"complete"} src={complete} height={24}/>;
            case AlertStatus.IN_PROGRESS:
                return <img alt={"action"} src={action} height={24}/>;
            default:
                return <div/>;
        }
    };

    private getStatusColor(status: AlertStatus) {
        switch (status) {
            case AlertStatus.OPEN:
                return COLORS.primary_blue;
            case AlertStatus.NEW:
                return COLORS.primary_blue;
            case AlertStatus.COMPLETE:
                return "#6d7278";
            case AlertStatus.IN_PROGRESS:
                return "#e2a703";
            default:
                return COLORS.neutral_text_dark;
        }
    }

    private resolveTooltipStatus = (alert: DB_Alert<any>) => {
        return alert.status === AlertStatus.NEW ? "Open" : formatStatus(alert.status);
    };

    private resolvePatient = (patientId?: string): DB_Patient | undefined => {
        return patientId ? this.props.patientsContext.patients[patientId] : undefined;
    };
    // patientId on DB_Alert may be undefined for org-level alerts.

    private sortLogic = (aVariable?: string | number, bVariable?: string | number, invert?: boolean): -1 | 0 | 1 => {
        return (this.sortImpl(aVariable, bVariable) * (invert ? -1 : 1)) as -1 | 1 | 0;
    };

    private sortImpl = (aVariable?: string | number, bVariable?: string | number): -1 | 0 | 1 => {
        if (!aVariable && !!bVariable)
            return 1;
        if (!bVariable && !!aVariable)
            return -1;
        if (!aVariable && !bVariable)
            return 0;
        // @ts-ignore
        if (aVariable > bVariable)
            return -1;
        return 1;
    };

    private sortByDate = (content: AlertsTableRow[]) => {
        return content.sort((a, b) => {
            switch (this.state.sort.filterColumn) {
                case "lastUpdated":
                    return this.sortLogic(a.lastUpdated?._updated.auditAt.timestamp, b.lastUpdated?._updated.auditAt.timestamp, this.state.sort.invert);
                case "created":
                default:
                    return this.sortLogic(a.created?._created.auditAt.timestamp, b.created?._created.auditAt.timestamp, this.state.sort.invert);
            }
        });
    };

    private renderPrettySubject(alert: DB_Alert<any>) {
        const {subject, details, _id: id} = alert;
        const subjectStyle: React.CSSProperties = {
            color: COLORS.black,
            fontWeight: 100,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: "40vw"
        };
        const commentBadge = CommentsModule.getNumberOfComments(id) !== 0 && <><HGap h={8}/>
            <img alt={"comment"} src={comment} height={16} style={{paddingTop: "2px"}}/></>;

        // Check-in alerts are org-scoped (no patientId) and we deliberately drop
        // the hover: at 100+ units the full name list is unreadable, and the
        // subject already carries the meaningful summary (names up to 3, count
        // form past that, or the all-done message).
        if (alert.alertType === CheckinAlertType_Missed || alert.alertType === CheckinAlertType_AllDone) {
            const names = (details || "").split(",").map(s => s.trim()).filter(Boolean);
            const bold = (n: string) => <span style={{fontFamily: "Inter-Bold"}}>{n}</span>;
            const suffix = " have not checked in yet this morning";
            const singularSuffix = " has not checked in yet this morning";

            let body: React.ReactNode;
            if (alert.alertType === CheckinAlertType_Missed && names.length === 1) body = <>{bold(names[0])}{singularSuffix}</>;
            else if (alert.alertType === CheckinAlertType_Missed && names.length === 2) body = <>{bold(names[0])} and {bold(names[1])}{suffix}</>;
            else if (alert.alertType === CheckinAlertType_Missed && names.length === 3) body = <>{bold(names[0])}, {bold(names[1])} and {bold(names[2])}{suffix}</>;
            else body = subject;

            return <div className={"ll_h_c"}>
                <div style={{...subjectStyle, fontFamily: "Inter-Regular", fontSize: 16}}>
                    {body}
                </div>
                {commentBadge}
            </div>;
        }

        const prettySubject: { [key: string]: string } = {
            "Prescription pickup not confirmed": "'s prescription pickup has not been confirmed",
            "Customer contact request": " requested to be contacted",
            "bad mood": " is reporting bad mood",
            "Energy level changed": "'s energy level has changed",
            "Customer cohort expired": "'s cohort expired",
            "Prescription not picked up": " has not picked up prescription",
            "Feeling worse": " is feeling worse",
            "Pain level changed": "'s pain level has changed",
            "Medication not started": " has not started medication",
            "bad sleep": " is reporting bad sleep",
            "Patient contact request": "Customer contact request"
        };

        return <div className={`ll_h_c`}>
            <div style={{paddingRight: "8px"}}>{
                FONTS.bold_16(this.resolvePatient(alert.patientId)?.name || "")
            }</div>
            <div onMouseEnter={(e) => showTooltip(e, details?.replace("\\n", ". "))}
                 onMouseLeave={() => hideTooltip()}>
                {FONTS.reg_16(prettySubject[subject] ? prettySubject[subject] : subject, subjectStyle)}
            </div>
            {commentBadge}
        </div>;
    }

    rerouteToPatientDashboard = (patients: PatientsMap, id?: string, alertId?: string, alertType?: string) => {
        // Check-in alerts are org-scoped — no patient lookup needed.
        if (alertType === CheckinAlertType_Missed || alertType === CheckinAlertType_AllDone) {
            RoutingModule.goToRoute(Route_CheckIn);
            return;
        }
        if (!id)
            return;
        const patient = patients[id];
        if (!patient)
            return;

        navigateToPatientPage(patient, Route_Alerts, alertId);
    };

    getLastUpdated = (alert: DB_Alert<any>) => {
        if (!alert._updated?.auditAt.timestamp)
            return alert._created.auditAt.timestamp;
        return Math.max(alert._created.auditAt.timestamp, alert._updated?.auditAt.timestamp);
    };

    calculatePagination = () => {
        const clientHeight = this.tableRef.current?.offsetHeight;
        if (!clientHeight)
            return {startIndex: 0, endIndex: 1};
        const pagination = Math.floor((clientHeight - headHeight) / rowHeight);
        const startIndex = (this.state.page - 1) * pagination;
        const endIndex = startIndex + pagination;
        return {startIndex, endIndex};
    };

    renderTable = (content: AlertsTableRow[]) => {
        return <PatientsContext.Consumer>{
            ({patients}) => <div
                ref={this.tableRef}
                className={"match_height match_width"}
                style={{...tableCardStyle,overflowY: "auto"}}
            >
                    <TS_Table<AlertsTableRow>
                        id={"all-patients"}
                        header={this.header}
                        rows={content}
                        trHead={{style: {boxShadow: "0 0 6px 3px #f4f4f4"}}}
                        tr={(val: AlertsTableRow, row: number) => {
                            return {
                                style: {
                                    borderBottom: `1px solid #f1f5f8`,
                                    cursor: "pointer"
                                },
                                className: emotion.css(
                                    {
                                        backgroundColor: val.alert?.status === AlertStatus.NEW ? "#fffaf0" : "transparent",
                                        ":hover": {backgroundColor: "#f1f5f8"}
                                    }
                                )
                            };
                        }}
                        td={{
                            style: {borderBottom: `1px solid ${COLORS.color_gray_medium}`, cursor: "pointer"}
                        }}
                        th={(key) => {
                            const className = emotion.css({
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                height: 34,
                                backgroundColor: COLORS.white,
                                minWidth: "100px"
                            });
                            return {className};
                        }}
                        style={{width: "100%"}}
                        headerRenderer={{
                            created: () => <TableHeader color={COLORS.white} title={"Created"} column={"created"}
                                                        options={{sortable: true}} sort={this.sortObj("created")}/>,
                            lastUpdated: () => <TableHeader color={COLORS.white} title={"Last updated"} column={"lastUpdated"}
                                                            options={{sortable: true}}
                                                            sort={this.sortObj("lastUpdated")}/>,
                            alert: () => <TableHeader color={COLORS.white} title={"Notifications"}/>,
                            careManager: () => <TableHeader color={COLORS.white} title={"Care manager"}
                                                            column={"careManager"}/>,
                            status: () => <TableHeader color={COLORS.white} title={"Notification status"} column={"status"}/>
                        }}
                        cellRenderer={{
                            alert: (alert?: DB_Alert<any>) => (
                                <div className={padding_rl_med} style={{display: "flex"}} onClick={() => {
                                    if (alert?.status === AlertStatus.NEW)
                                        this.rerouteToPatientDashboard(patients, alert?.patientId, undefined, alert?.alertType);
                                    else
                                        this.rerouteToPatientDashboard(patients, alert?.patientId, alert?._id, alert?.alertType);
                                }}>
                                    {
                                        alert && <div style={{display: "flex"}}>
                                            <div style={{width: 24, height: 24}}
                                                 onMouseEnter={(e) => showTooltip(e, this.resolveTooltipStatus(alert))}
                                                 onMouseLeave={() => hideTooltip()}>
                                                {this.resolveIcon(alert)}
                                            </div>
                                            <HGap h={6}/>
                                            {this.renderPrettySubject(alert)}
                                        </div>
                                    }
                                </div>),
                            careManager: (value: { careManager?: DB_User, bold?: boolean, alert?: DB_Alert<any> }) => <div
                                onClick={() => {
                                    if (value.alert?.status === AlertStatus.NEW)
                                        this.rerouteToPatientDashboard(patients, value.alert?.patientId, undefined, value.alert?.alertType);
                                    else
                                        this.rerouteToPatientDashboard(patients, value.alert?.patientId, value.alert?._id, value.alert?.alertType);
                                }}>
                                {
                                    <TableContent
                                        content={value.careManager ? getUserNameWithTitle(value.careManager) : ""}/>
                                }
                            </div>,
                            status: (alert?: DB_Alert<any>) => {
                                // In case the status is RECEIVED, display it as OPEN.
                                return alert && alert.status &&
                                    <div onClick={() => {
                                        if (alert?.status === AlertStatus.NEW)
                                            this.rerouteToPatientDashboard(patients, alert?.patientId, undefined, alert?.alertType);
                                        else
                                            this.rerouteToPatientDashboard(patients, alert?.patientId, alert?._id, alert?.alertType);
                                    }}>
                                        <TableContent
                                            content={formatStatus(alert.status)}
                                            color={this.getStatusColor(alert.status)}/>
                                    </div>;
                            },
                            lastUpdated: (value?: DB_Alert<any>) => {
                                return value?._created.auditAt && <div style={{
                                    height: "35px"
                                }} className="ll_h_c" onClick={() => {
                                    if (value.status === AlertStatus.NEW)
                                        this.rerouteToPatientDashboard(patients, value.patientId, undefined, value.alertType);
                                    else
                                        this.rerouteToPatientDashboard(patients, value.patientId, value._id, value.alertType);
                                }}>

                                    <TableContent
                                        content={DateTimesUtil.alertsPresentation(this.getLastUpdated(value))}
                                        id={value.patientId}
                                        onClick={(id) => this.rerouteToPatientDashboard(patients, id)}/>

                                </div>
                            },
                            created: (value?: DB_Alert<any>) => {
                                return value?._created.auditAt && <div
                                    style={{
                                        height: "35px"
                                    }}
                                    className={`ll_h_c`}
                                    onClick={() => {
                                        if (value.status === AlertStatus.NEW)
                                            this.rerouteToPatientDashboard(patients, value.patientId, undefined, value.alertType);
                                        else
                                            this.rerouteToPatientDashboard(patients, value.patientId, value._id, value.alertType);
                                    }}
                                >
                                    <TableContent
                                        content={DateTimesUtil.alertsPresentation(value._created.auditAt.timestamp)}
                                        id={value?.patientId}
                                        onClick={(id) => this.rerouteToPatientDashboard(patients, id)}
                                    />
                                </div>
                            }
                        }}
                    />
                </div>
                }</PatientsContext.Consumer>
        }

            render() {
                const tableContent = this.sortByDate(this.props.content);
                const pagination = calculatePagination(this.state.page, this.tableRef.current);
        const filteredContent = tableContent.slice(pagination.startIndex, pagination.endIndex);

        return <>
            {this.renderTable(filteredContent)}
            <PaginationComponent
                pagination={pagination}
                tableContent={tableContent}
                setCurrentPage={(page: number) => this.setState({page})}
            />
        </>
    }

}
